import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Card,
  Col,
  Form,
  Layout,
  Popover,
  Radio,
  Row,
  Spin,
  Typography,
} from "antd";
import {
  HomeOutlined,
  ShoppingCartOutlined,
  UnorderedListOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { ChromePicker, GithubPicker } from "react-color";

import { ApiManager, Locale } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import Base from "../Base";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();
const base = Base.getInstance();
const { Header, Content, Footer } = Layout;
const { Paragraph } = Typography;

const DEFAULTS = {
  primaryColor: "#e1251b",
  secondaryColor: "#ffffff",
  navBarBgColor: "#e1251b",
  navBarTextColor: "#ffffff",
  theme: "light",
};

function AppearanceSetting() {
  const user = useSelector((state) => getUserInfo(state));
  const token = user?.token;
  const merchantId = user?.merchant?.id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [primaryColor, setPrimaryColor] = useState(DEFAULTS.primaryColor);
  const [secondaryColor, setSecondaryColor] = useState(DEFAULTS.secondaryColor);
  const [navBarBgColor, setNavBarBgColor] = useState(DEFAULTS.navBarBgColor);
  const [navBarTextColor, setNavBarTextColor] = useState(
    DEFAULTS.navBarTextColor
  );
  const [theme, setTheme] = useState(DEFAULTS.theme);
  const loadedForMerchantRef = useRef(null);

  const applySettings = useCallback(
    (data = {}) => {
      const nextPrimary = data.app_color || DEFAULTS.primaryColor;
      const nextSecondary = data.font_color || DEFAULTS.secondaryColor;
      const nextNavBg = data.nav_bg_color || nextPrimary;
      const nextNavText = data.nav_text_color || nextSecondary;
      const nextTheme = data.theme || DEFAULTS.theme;

      setPrimaryColor(nextPrimary);
      setSecondaryColor(nextSecondary);
      setNavBarBgColor(nextNavBg);
      setNavBarTextColor(nextNavText);
      setTheme(nextTheme);
      form.setFieldsValue({ theme: nextTheme });
    },
    [form]
  );

  const loadSettings = useCallback(async () => {
    if (!token || !merchantId) return;

    setLoading(true);
    try {
      const res = await api.user.merchantSettings.run({
        token,
        path: { merchant_id: merchantId },
      });
      if (!res.result) {
        base.showMessage(res.message, Base.Message.error);
        return;
      }
      applySettings(res.data || {});
    } catch (error) {
      base.handleApiError(api.user.merchantSettings, error);
    } finally {
      setLoading(false);
    }
  }, [applySettings, merchantId, token]);

  useEffect(() => {
    if (!token || !merchantId) return;
    if (loadedForMerchantRef.current === merchantId) return;
    loadedForMerchantRef.current = merchantId;
    loadSettings();
  }, [loadSettings, merchantId, token]);

  const handleSubmit = async () => {
    if (!token) return;

    setSaving(true);
    try {
      const values = form.getFieldsValue();
      const res = await api.setting.update.run({
        token,
        data: {
          group: "shop_info",
          data: {
            app_color: primaryColor,
            font_color: secondaryColor,
            theme: values.theme || theme,
            nav_bg_color: navBarBgColor,
            nav_text_color: navBarTextColor,
          },
        },
      });
      if (res.result) {
        base.showMessage(
          res.message || i18n.t("media.saveFinished"),
          Base.Message.success
        );
        await loadSettings();
      } else {
        base.showMessage(res.message, Base.Message.error);
      }
    } catch (error) {
      base.handleApiError(api.setting.update, error);
    } finally {
      setSaving(false);
    }
  };

  const previewBg = theme === "dark" ? "#111" : "#fff";

  return (
    <Spin spinning={loading || saving}>
      <Card title={i18n.t("appearance.title")}>
        <Paragraph type="secondary">{i18n.t("appearance.pageHint")}</Paragraph>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <Form
              form={form}
              labelCol={{ span: 8 }}
              wrapperCol={{ span: 14 }}
              initialValues={{ theme: DEFAULTS.theme }}
              onFinish={handleSubmit}
            >
              <Form.Item label={i18n.t("sales.mini.primary")}>
                <Popover
                  content={
                    <ChromePicker
                      color={primaryColor}
                      onChange={(color) => setPrimaryColor(color.hex)}
                    />
                  }
                  placement="right"
                >
                  <div
                    style={{
                      backgroundColor: primaryColor,
                      width: 50,
                      height: 20,
                      border: "1px solid #333",
                      cursor: "pointer",
                    }}
                  />
                </Popover>
              </Form.Item>
              <Form.Item label={i18n.t("sales.mini.navBarBgColor")}>
                <Popover
                  content={
                    <ChromePicker
                      color={navBarBgColor}
                      onChange={(color) => setNavBarBgColor(color.hex)}
                    />
                  }
                  placement="right"
                >
                  <div
                    style={{
                      backgroundColor: navBarBgColor,
                      width: 50,
                      height: 20,
                      border: "1px solid #333",
                      cursor: "pointer",
                    }}
                  />
                </Popover>
              </Form.Item>
              <Form.Item label={i18n.t("sales.mini.navBarTextColor")}>
                <Popover
                  content={
                    <ChromePicker
                      color={navBarTextColor}
                      onChange={(color) => setNavBarTextColor(color.hex)}
                    />
                  }
                  placement="right"
                >
                  <div
                    style={{
                      backgroundColor: navBarTextColor,
                      width: 50,
                      height: 20,
                      border: "1px solid #333",
                      cursor: "pointer",
                    }}
                  />
                </Popover>
              </Form.Item>
              <Form.Item
                name="theme"
                label={i18n.t("sales.mini.theme")}
              >
                <Radio.Group
                  buttonStyle="solid"
                  onChange={(e) => setTheme(e.target.value)}
                >
                  <Radio.Button value="dark">
                    {i18n.t("sales.mini.dark")}
                  </Radio.Button>
                  <Radio.Button value="light">
                    {i18n.t("sales.mini.light")}
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
              <Form.Item label={i18n.t("sales.mini.secondary")}>
                <Popover
                  placement="right"
                  content={
                    <GithubPicker
                      triangle="hide"
                      colors={["#ffffff", "#333333"]}
                      onChange={(color) => setSecondaryColor(color.hex)}
                    />
                  }
                >
                  <div
                    style={{
                      backgroundColor: secondaryColor,
                      width: 50,
                      height: 20,
                      border: "1px solid #333",
                      cursor: "pointer",
                    }}
                  />
                </Popover>
              </Form.Item>
              <Form.Item wrapperCol={{ offset: 8 }}>
                <Button type="primary" htmlType="submit" loading={saving}>
                  {i18n.t("sales.submit")}
                </Button>
              </Form.Item>
            </Form>
          </Col>
          <Col xs={24} md={12}>
            <div
              style={{
                border: "1px solid #eee",
                marginBottom: 20,
                backgroundColor: previewBg,
                width: 375,
                height: 640,
                overflow: "hidden",
                boxSizing: "border-box",
              }}
            >
              <Layout
                style={{
                  backgroundColor: previewBg,
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Header
                  style={{
                    backgroundColor: navBarBgColor,
                    color: navBarTextColor,
                    padding: "0 16px",
                    height: 44,
                    lineHeight: "44px",
                    fontSize: 16,
                    flex: "none",
                  }}
                >
                  {i18n.t("appearance.preview")}
                </Header>
                <Content
                  style={{
                    flex: 1,
                    padding: 20,
                    backgroundColor: previewBg,
                  }}
                >
                  <Button
                    type="primary"
                    style={{
                      backgroundColor: primaryColor,
                      borderColor: primaryColor,
                      color: secondaryColor,
                      height: 32,
                      fontSize: 14,
                      padding: "0 20px",
                    }}
                  >
                    {i18n.t("sales.submit")}
                  </Button>
                </Content>
                <Footer
                  style={{
                    backgroundColor: previewBg,
                    padding: "12px 0",
                    borderTop: "1px solid #eee",
                    flex: "none",
                  }}
                >
                  <Row justify="space-around">
                    <HomeOutlined style={{ color: primaryColor, fontSize: 18 }} />
                    <UnorderedListOutlined style={{ color: "#999", fontSize: 18 }} />
                    <ShoppingCartOutlined style={{ color: "#999", fontSize: 18 }} />
                    <UserOutlined style={{ color: "#999", fontSize: 18 }} />
                  </Row>
                </Footer>
              </Layout>
            </div>
          </Col>
        </Row>
      </Card>
    </Spin>
  );
}

export default AppearanceSetting;
